import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileText, Users, Database, Clock, Infinity, ChevronDown, ChevronUp } from "lucide-react";

import { Button }              from "../components/ui/button";
import { Switch }              from "../components/ui/switch";
import { Label }               from "../components/ui/label";
import { Slider }              from "../components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const SOURCES = [
  { key: "bank",     label: "tmuaprep Bank", sub: "Curated questions",     Icon: Database },
  { key: "official", label: "Official TMUA", sub: "Past papers 2016–2025", Icon: FileText },
  { key: "custom",   label: "Community",     sub: "Contributed questions",  Icon: Users    },
];

const MODES = [
  { value: "timed",   label: "Timed",   sub: "3.5 min per", Icon: Clock    },
  { value: "untimed", label: "Untimed", sub: "No limit — focus on understanding",     Icon: Infinity },
];

const PAPERS = [
  { value: "both", label: "Both" },
  { value: "1",    label: "P1"   },
  { value: "2",    label: "P2"   },
];

const TOPICS = ["Algebra", "Calculus", "Geometry", "Probability", "Sequences", "Trigonometry", "Functions", "Logic", "Number Theory"];
const PRESETS = [5, 10, 20, 40];

const SectionLabel = ({ children }) => (
  <span className="eyebrow text-[var(--muted-text)]">{children}</span>
);

const QuestionConfig = () => {
  const navigate = useNavigate();
  const [sources,        setSources]        = useState({ bank: true, official: false, custom: false });
  const [timedMode,      setTimedMode]      = useState("timed");
  const [numQuestions,   setNumQuestions]   = useState(10);
  const [paper,          setPaper]          = useState("both");
  const [topicOpen,      setTopicOpen]      = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);

  const toggleTopic = t =>
    setSelectedTopics(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );

  const canStart = Object.values(sources).some(Boolean);
  const toggle   = key => setSources(s => ({ ...s, [key]: !s[key] }));

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>

      {/* Nav */}
      <nav className="border-b border-[var(--border-ui)] sticky top-0 bg-[var(--bg)]/95 backdrop-blur-md z-10 px-6">
        <div className="py-3.5 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="brand-btn">
            tmua<span className="brand-prep">prep</span>
          </button>
          <button onClick={() => navigate("/practice")} className="text-[var(--muted-text)] hover:text-[var(--text)] text-xs font-medium transition-colors flex items-center gap-1.5">← Back</button>
        </div>
      </nav>

      <main className="paper-grid flex-1 px-6 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="mb-8">
            <p className="eyebrow text-[#60a5fa] mb-2">Practice</p>
            <h1 className="font-display text-[36px] tracking-tight mb-1">Configure your session</h1>
            <p className="text-[var(--muted-text)] text-sm">Choose your sources, mode, and question count.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-5 items-start">

          {/* Left — config panels */}
          <div className="lg:col-span-3 rounded-3xl border border-[var(--border-ui)] overflow-hidden bg-[var(--surface)] shadow-[var(--shadow)]">

            {/* ── Sources ── */}
            <div className="px-5 pt-4 pb-1">
              <SectionLabel>Sources</SectionLabel>
            </div>
            <div>
              {SOURCES.map((s, i) => (
                <div
                  key={s.key}
                  className={`flex items-center justify-between px-5 py-3 transition-colors ${sources[s.key] ? "bg-[var(--soft-fill)]" : ""} ${i < SOURCES.length - 1 ? "border-b border-[var(--border-ui)]" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <s.Icon className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${sources[s.key] ? "text-[#60a5fa]" : "text-[var(--muted-text)]"}`} strokeWidth={1.5} />
                    <Label htmlFor={`src-${s.key}`} className="cursor-pointer">
                      <div className={`text-sm font-medium transition-colors ${sources[s.key] ? "text-[var(--text)]" : "text-[var(--muted-text)]"}`}>
                        {s.key === "bank" ? <span>tmua<span className="text-[#60a5fa]">prep</span> Bank</span> : s.label}
                      </div>
                      <div className="text-[11px] text-[var(--muted-text)] mt-0.5">{s.sub}</div>
                    </Label>
                  </div>
                  <Switch id={`src-${s.key}`} checked={sources[s.key]} onCheckedChange={() => toggle(s.key)} />
                </div>
              ))}
            </div>
            {!canStart && (
              <div className="px-5 py-2 bg-[var(--soft-fill)] text-[var(--subtle-text)] text-[11px]">
                Select at least one source to continue.
              </div>
            )}

            <div className="h-px bg-[var(--soft-fill)] mx-0" />

            {/* ── Paper + Mode side by side ── */}
            <div className="grid grid-cols-2 divide-x divide-[var(--border-ui)]">
              <div className="px-5 py-4">
                <SectionLabel>Paper</SectionLabel>
                <div className="mt-3">
                  <Tabs value={paper} onValueChange={setPaper}>
                    <TabsList className="w-full h-9">
                      {PAPERS.map(p => (
                        <TabsTrigger key={p.value} value={p.value} className="flex-1 h-full whitespace-nowrap">{p.label}</TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>
              </div>
              <div className="px-5 py-4">
                <SectionLabel>Mode</SectionLabel>
                <div className="mt-3">
                  <Tabs value={timedMode} onValueChange={setTimedMode}>
                    <TabsList className="w-full h-9">
                      {MODES.map(m => (
                        <TabsTrigger key={m.value} value={m.value} className="flex-1 h-full whitespace-nowrap">
                          <m.Icon className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                          {m.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                  <p className="text-[10px] text-[var(--muted-text)] mt-2 leading-relaxed">
                    {MODES.find(m => m.value === timedMode)?.sub}
                  </p>
                </div>
              </div>
            </div>

            <div className="h-px bg-[var(--soft-fill)]" />

            {/* ── Topics ── */}
            <button
              className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-[var(--soft-fill)] transition-colors"
              onClick={() => setTopicOpen(o => !o)}
            >
              <div className="flex items-center gap-2.5">
                <SectionLabel>Topics</SectionLabel>
                <span className="text-[11px] text-[var(--muted-text)]">
                  {selectedTopics.length === 0 ? "All" : selectedTopics.length === 1 ? selectedTopics[0] : `${selectedTopics.length} selected`}
                </span>

              </div>
              {topicOpen ? <ChevronUp className="w-3.5 h-3.5 text-[var(--muted-text)]" /> : <ChevronDown className="w-3.5 h-3.5 text-[var(--muted-text)]" />}
            </button>
            {topicOpen && (
              <div className="px-5 pb-4 border-t border-[var(--border-ui)] pt-3">
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setSelectedTopics([])}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${selectedTopics.length === 0 ? "bg-[#3b82f6] text-white" : "bg-[var(--soft-fill)] text-[var(--muted-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill-strong)]"}`}
                  >
                    All
                  </button>
                  {TOPICS.map(t => (
                    <button
                      key={t}
                      onClick={() => toggleTopic(t)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${selectedTopics.includes(t) ? "bg-[#3b82f6] text-white" : "bg-[var(--soft-fill)] text-[var(--muted-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill-strong)]"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="h-px bg-[var(--soft-fill)]" />

            {/* ── Questions ── */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-4">
                <SectionLabel>Questions</SectionLabel>
                <span className="num text-sm font-semibold text-[var(--text)]">
                  {numQuestions}
                  <span className="text-[var(--muted-text)] font-normal text-xs ml-1">/ 100</span>
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {PRESETS.map(n => (
                  <Button
                    key={n}
                    size="sm"
                    variant={numQuestions === n ? "selected" : "outline"}
                    onClick={() => setNumQuestions(n)}
                  >
                    {n}
                  </Button>
                ))}
              </div>
              <Slider min={1} max={100} step={1} value={[numQuestions]} onValueChange={([v]) => setNumQuestions(v)} />
              <div className="flex justify-between text-[10px] text-[var(--muted-text)] mt-1.5">
                <span>1</span><span>100</span>
              </div>
            </div>

          </div>

          {/* Right — session ticket */}
          <aside className="lg:col-span-2 lg:sticky lg:top-20">
            <div className="rounded-3xl border border-[#60a5fa]/[0.24] bg-[var(--surface)] overflow-hidden shadow-[var(--shadow)]">

              {/* ticket header with barcode motif */}
              <div className="px-5 pt-5 pb-4 flex items-start justify-between">
                <div>
                  <p className="eyebrow text-[#60a5fa] mb-1">Session Ticket</p>
                  <p className="num text-[10px] text-[var(--faint-text)]">tmuaprep · practice</p>
                </div>
                <div className="flex items-end gap-[2px] h-6 opacity-40" aria-hidden="true">
                  {[3, 1, 2, 1, 3, 2, 1, 3, 1, 2, 3, 1].map((w, i) => (
                    <span key={i} className="bg-[#93c5fd] h-full" style={{ width: w }} />
                  ))}
                </div>
              </div>

              {/* perforation */}
              <div className="border-t border-dashed border-[var(--border-ui)] relative">
                <span className="absolute -left-2 -top-2 w-4 h-4 rounded-full bg-[var(--bg)]" />
                <span className="absolute -right-2 -top-2 w-4 h-4 rounded-full bg-[var(--bg)]" />
              </div>

              {/* summary rows */}
              <div className="px-5 py-4 space-y-3">
                {[
                  { k: "Sources", v: Object.entries(sources).filter(([, on]) => on).map(([k]) => SOURCES.find(s => s.key === k)?.label.replace("tmuaprep ", "")).join(", ") || "None selected", warn: !canStart },
                  { k: "Paper", v: paper === "both" ? "Paper 1 & 2" : `Paper ${paper}` },
                  { k: "Mode", v: timedMode === "timed" ? "Timed" : "Untimed" },
                  { k: "Topics", v: selectedTopics.length === 0 ? "All topics" : `${selectedTopics.length} selected` },
                  { k: "Questions", v: String(numQuestions) },
                  { k: "Est. duration", v: timedMode === "timed" ? (() => { const m = Math.round(numQuestions * 3.5); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`; })() : "Open-ended" },
                ].map(row => (
                  <div key={row.k} className="flex items-baseline justify-between gap-3">
                    <span className="eyebrow !text-[9px] text-[var(--subtle-text)] flex-shrink-0">{row.k}</span>
                    <span className="flex-1 border-b border-dotted border-[var(--border-ui)] relative -top-0.5" />
                    <span className={`num text-[12px] text-right ${row.warn ? "text-[#f87171]" : "text-[var(--text-soft)]"}`}>{row.v}</span>
                  </div>
                ))}
              </div>

              {/* start */}
              <div className="px-5 pb-5 pt-2">
                <Button
                  className="group w-full !py-5"
                  variant="cta"
                  size="default"
                  disabled={!canStart}
                  onClick={() => navigate("/exam-select", {
                    state: {
                      numQuestions,
                      includeBank:     sources.bank,
                      includeOfficial: sources.official,
                      includeCustom:   sources.custom,
                      timedMode:       timedMode === "timed",
                      paper:           paper === "both" ? undefined : parseInt(paper),
                      topics:          selectedTopics,
                    },
                  })}
                >
                  Start session
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
                {!canStart && <p className="text-[10px] text-[var(--subtle-text)] text-center mt-2.5">Select at least one source to continue.</p>}
              </div>
            </div>
          </aside>

          </div>
        </div>
      </main>

      <footer className="border-t border-[var(--border-ui)] px-6 py-5">
        <p className="text-center text-[var(--subtle-text)] text-xs">© 2025 TMUA Prep. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default QuestionConfig;
